import React, { useMemo } from 'react';
import {
  BucketDefType,
  BucketKind,
  INamedBucketDefFieldsFragment,
  ITagOptionInput,
  TagOption,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  removeBinning,
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../../redux/slices/oldBuilder';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { builderBinningSelector } from '../../../../../redux/selectors/binning';
import {
  IAutoRangeBinning,
  IBinning,
  IDateBinning,
  IEnumBinning,
  INamedBinning,
} from '../../../../../redux/slices/oldBuilder/binning/types';
import { INITIAL_BIN_COUNT } from '../utils';
import { assertCondition, assertNotNil } from '../../../../../utils/types';
import EntitySelection from '../../../EntitySelection';
import { createTagOptionInput } from '../../../EntitySelection/utils';
import {
  IRemoveEntityHandler,
  ISelectEntityHandler,
  ISubsetSelectedEntity,
} from '../../../EntitySelection/types';
import { isNil } from 'lodash';
import {
  defaultAutoRangeBucket,
  defaultEnumBucket,
} from '../../../../../redux/slices/oldBuilder/binning/defaults';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  activeSchemaId: string;
  groupIndex: number | undefined;
  supportedBucketKinds: BucketKind[];
  selectedFieldKey: string | null;
  onSelectNamedBucket: (
    namedBucketDef: INamedBucketDefFieldsFragment | null,
  ) => void;
}

type IBinningSelectedEntities = ISubsetSelectedEntity<
  'NamedBucketDef' | '__CustomBucketDef'
>;

/**
 * Binning Selector is the dropdown which contains named bucket and also bucket kinds.
 * This is the single select that user can either select bucket kind to manually input a binning
 * or selecting an existing named binning.
 *
 * Each breakdown group should provide selectedFieldKey
 * that we will use to fetch the named buckets related to it
 */
export default ({
  activeSchemaId,
  groupIndex,
  supportedBucketKinds,
  selectedFieldKey,
  onSelectNamedBucket,
}: IProps) => {
  const dispatch = useAppDispatch();

  const selectedBinning = useAppSelector((state) =>
    builderBinningSelector(state, groupIndex),
  );

  const handleSelectBinning: ISelectEntityHandler = (selectedOption) => {
    // handling named option
    assertCondition(
      selectedOption.__typename === '__CustomBucketDef' ||
        selectedOption.__typename === 'NamedBucketDef',
    );
    if (selectedOption.__typename === 'NamedBucketDef') {
      const namedBucketDef = selectedOption;
      onSelectNamedBucket(namedBucketDef);

      const newNamedBinning = {
        namedBucketDef,
        type: BucketDefType.Named,
        kind: namedBucketDef.kind,
        updateKey: 0,
      } satisfies INamedBinning;

      if (isNil(groupIndex)) {
        dispatch(
          setSelectedBinning({
            newBinning: newNamedBinning,
          }),
        );
      } else {
        dispatch(
          setBreakdownGroupBinning({
            groupIndex,
            newBinning: newNamedBinning,
          }),
        );
      }

      return;
    }

    onSelectNamedBucket(null);

    // handling basic option
    const kind = selectedOption.kind;

    let newBinning: IBinning;
    switch (kind) {
      case BucketKind.Date: {
        newBinning = {
          kind,
          type: BucketDefType.Basic,
          customBuckets: [],
          updateKey: 0,
        } satisfies IDateBinning;
        break;
      }
      case BucketKind.Enum: {
        let customBuckets: IEnumBinning['customBuckets'] = [];
        for (let i = 0; i < INITIAL_BIN_COUNT; i++) {
          customBuckets.push(defaultEnumBucket);
        }
        newBinning = {
          kind,
          type: BucketDefType.Basic,
          // we will set default and empty enum buckets for enum binning
          customBuckets,
          enumBucketSplitOthers: false,
          updateKey: 0,
        } satisfies IEnumBinning;
        break;
      }
      case BucketKind.Range: {
        newBinning = {
          kind,
          type: BucketDefType.Basic,
          customBuckets: [],
          updateKey: 0,
          isAutoRangeBucket: true,
          autoRangeBucket: defaultAutoRangeBucket,
        } satisfies IAutoRangeBinning;
        break;
      }
      default:
        throw new UnreachableCaseError(kind);
    }

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  const tags = useMemo((): ITagOptionInput[] => {
    const tags: ITagOptionInput[] = [];
    if (supportedBucketKinds.includes(BucketKind.Enum)) {
      assertNotNil(selectedFieldKey);
      tags.push(
        createTagOptionInput(TagOption.EnumBucketDef, {
          bucketDef: { field: selectedFieldKey },
        }),
      );
    }
    if (supportedBucketKinds.includes(BucketKind.Date)) {
      tags.push(createTagOptionInput(TagOption.DateBucketDef));
    }
    if (supportedBucketKinds.includes(BucketKind.Range)) {
      tags.push(createTagOptionInput(TagOption.RangeBucketDef));
    }
    return tags;
  }, [selectedFieldKey, supportedBucketKinds]);

  const selectedEntities = useMemo((): IBinningSelectedEntities[] => {
    if (isNil(selectedBinning)) {
      return [];
    }
    if (selectedBinning.type === BucketDefType.Basic) {
      return [{ __typename: '__CustomBucketDef', kind: selectedBinning.kind }];
    } else if (selectedBinning.type === BucketDefType.Named) {
      return [selectedBinning.namedBucketDef];
    } else {
      throw new UnreachableCaseError(selectedBinning);
    }
  }, [selectedBinning]);

  const handleRemoveBinning: IRemoveEntityHandler = () => {
    if (isNil(groupIndex)) {
      dispatch(removeBinning());
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning: null,
        }),
      );
    }
  };

  return (
    <EntitySelection
      selectedEntities={selectedEntities}
      schemaId={activeSchemaId}
      onRemoveEntity={handleRemoveBinning}
      onSelectEntity={handleSelectBinning}
      tags={tags}
      autoFocus={isNil(selectedBinning)}
      placeholder="Binning"
    />
  );
};
